import {useMemo} from 'react'
import {useQuery} from '@tanstack/react-query'
import {Plus, Clock, CheckCircle, Ban} from 'lucide-react'
import {useNavigate} from 'react-router-dom'
import {usePageTitle} from '@/hooks/use-page-title'
import {useAuth} from '@/contexts/auth-context'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {Icon} from '@/components/shared/Icon'
import {routes} from '@/lib/routes'
import {Button} from '@/components/ui/button'
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from '@/components/ui/table'
import {TableEmptyState} from '@/components/shared/TableEmptyState'
import {PageLayout} from '@/components/shared/PageLayout'
import {Badge} from '@/components/ui/badge'
import {Pagination} from '@/components/ui/pagination'
import {useServerPagination, SortDir} from '@/hooks/use-server-pagination'
import {FunctionGroupRequestControllerRequestsEndpoint} from '@/types/endpoint-contracts'
import {RequestStatus} from '@/types/function-group'
import {permissions} from '@/lib/permissions'
import {formatDateOnly} from '@/lib/date-utils'
import {UnifiedSearchBar} from '@/components/shared/UnifiedSearchBar'
import {ClickableTableRow} from '@/components/shared/ClickableTableRow'
import {TextLink} from '@/components/shared/TextLink'
import {MutedText} from '@/components/shared/MutedText'
import {TruncatedCell} from '@/components/shared/TruncatedCell'
import {CONCEPT_ICON} from '@/lib/concepts'

export function FunctionGroupRequests() {
    const services = useServices()
    const i18n = useI18n()
    usePageTitle(i18n.functionGroupRequests.pageTitle)
    const navigate = useNavigate()
    const {hasPermission} = useAuth()

    const getStatusBadge = (status: RequestStatus) => {
        switch (status) {
            case RequestStatus.SUBMITTED:
                return <Badge variant="outline" className="gap-1"><Clock className="h-3 w-3"/>{i18n.functionGroupRequests.statusSubmitted}</Badge>
            case RequestStatus.IN_PROGRESS:
                return <Badge variant="secondary" className="gap-1"><Clock className="h-3 w-3"/>{i18n.functionGroupRequests.statusInProgress}</Badge>
            case RequestStatus.COMPLETED:
                return <Badge variant="success" className="gap-1"><CheckCircle className="h-3 w-3"/>{i18n.functionGroupRequests.statusCompleted}</Badge>
            case RequestStatus.CANCELLED:
                return <Badge variant="destructive" className="gap-1"><Ban className="h-3 w-3"/>{i18n.functionGroupRequests.statusCancelled}</Badge>
        }
    }

    const {
        page, pageSize, queryString,
        setPage, setPageSize, pageSizeOptions,
        sortBy, sortDir,
        searchAndFilters, setSearchAndFilters,
    } = useServerPagination({
        endpoint: FunctionGroupRequestControllerRequestsEndpoint,
        sortable: true,
        defaultSortBy: FunctionGroupRequestControllerRequestsEndpoint.sort.CREATED_AT,
        defaultSortDir: SortDir.DESC,
    })

    // Issue 342.07 / spec §6 Phase C.2: every chip on the bar carries an active filter.
    // Empty URL means "no filters" — no implicit defaults (closes the 244 class of bugs).
    const {data, isLoading} = useQuery({
        queryKey: ['function-group-requests', queryString, page, pageSize, sortBy, sortDir],
        queryFn: () => services.functionGroupRequests.list(Object.fromEntries(new URLSearchParams(queryString))),
    })

    const totalItems = data?.totalElements || 0
    const totalPages = data?.totalPages || 0

    const hasActiveFilters = useMemo(() => {
        return !!searchAndFilters.q
            || Object.keys(searchAndFilters.filters).length > 0
            || Object.keys(searchAndFilters.dynamicAttributes).length > 0
    }, [searchAndFilters])

    return (
        <PageLayout
            variant="list"
            icon={CONCEPT_ICON.functionGroupRequest}
            title={i18n.functionGroupRequests.title}
            count={totalItems}
            data-testid="function-group-requests-list"
            actions={hasPermission(permissions.functionGroupRequests.create) ? (
                <Button data-testid="btn-create-fg-request" onClick={() => navigate(routes.accessGovernance.functionGroupRequests.create())}>
                    <Icon icon={Plus} className="mr-2"/>
                    {i18n.functionGroupRequests.createRequest}
                </Button>
            ) : undefined}
            filters={
                <UnifiedSearchBar
                    contract={FunctionGroupRequestControllerRequestsEndpoint}
                    value={searchAndFilters}
                    onChange={setSearchAndFilters}
                    placeholder={i18n.functionGroupRequests.searchByBeneficiary}
                />
            }
            pagination={totalItems > 0 ? (
                <Pagination
                    page={page}
                    pageSize={pageSize}
                    totalItems={totalItems}
                    totalPages={totalPages}
                    onPageChange={setPage}
                    onPageSizeChange={setPageSize}
                    pageSizeOptions={pageSizeOptions}
                />
            ) : undefined}
        >
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead data-testid="col-requester">{i18n.functionGroupRequests.requester}</TableHead>
                        <TableHead data-testid="col-beneficiary">{i18n.functionGroupRequests.beneficiary}</TableHead>
                        <TableHead data-testid="col-justification">{i18n.functionGroupRequests.justification}</TableHead>
                        <TableHead data-testid="col-items">{i18n.functionGroupRequests.items}</TableHead>
                        <TableHead data-testid="col-status">{i18n.common.status}</TableHead>
                        <TableHead data-testid="col-created-at">{i18n.common.createdAt}</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {isLoading && (
                        <TableEmptyState
                            colSpan={6}
                            hasFilters={false}
                            matchMessage={i18n.functionGroupRequests.loadingRequests}
                            emptyMessage={i18n.functionGroupRequests.loadingRequests}
                        />
                    )}
                    {data && data.content.length === 0 && (
                        <TableEmptyState
                            colSpan={6}
                            hasFilters={hasActiveFilters}
                            matchMessage={i18n.functionGroupRequests.noRequestsFound}
                            emptyMessage={i18n.functionGroupRequests.noRequestsFound}
                        />
                    )}
                    {data?.content.map(request => (
                        <ClickableTableRow key={request.id}
                                  to={routes.accessGovernance.functionGroupRequests.detail(request.id)}>
                            <TableCell>{request.requester.displayName}</TableCell>
                            <TableCell>
                                <TextLink testId={`link-fg-request-detail-${request.id}`} to={routes.accessGovernance.functionGroupRequests.detail(request.id)} variant="bold" stopPropagation>
                                    {request.beneficiary.displayName}
                                </TextLink>
                            </TableCell>
                            <TruncatedCell>
                                {request.justification}
                            </TruncatedCell>
                            <TableCell>{request.items.length}</TableCell>
                            <TableCell>{getStatusBadge(request.status)}</TableCell>
                            <TableCell>
                                <MutedText size="sm">{formatDateOnly(request.createdAt)}</MutedText>
                            </TableCell>
                        </ClickableTableRow>
                    ))}
                </TableBody>
            </Table>
        </PageLayout>
    )
}
